import { ZaparooClient } from '../../client/client.js';
import { resolvePaths } from '../../client/config.js';
import { resolveDevice } from '../../client/resolver.js';
import { TraceWriter } from '../../client/trace.js';
import { CredentialStore } from '../../crypto/storage.js';
import type { ParsedArgs } from '../args.js';
import { flag, numberFlag } from '../args.js';
import { CliError, ExitCode } from '../errors.js';
import type { CommandResult } from '../output.js';

export async function watchCommand(args: ParsedArgs): Promise<CommandResult> {
  const device = await resolveDevice(args.options);
  const paths = resolvePaths(args.options.configPath, args.options.credentialsPath);
  const credentials = new CredentialStore(paths.credentialsPath).getCredentials(
    device.id,
    device.aliases,
  );
  const client = new ZaparooClient(device, {
    credentials,
    connectTimeoutMs: args.options.timeoutSeconds * 1000,
    requestTimeoutMs: args.options.timeoutSeconds * 1000,
    trace: args.options.trace ? new TraceWriter() : undefined,
  });
  const seconds = numberFlag(args.flags, 'seconds') ?? args.options.timeoutSeconds;
  if (seconds <= 0 || seconds > 2_147_483) {
    throw new CliError('--seconds must be positive and no greater than 2147483', ExitCode.Usage);
  }
  const methods = new Set((flag(args.flags, 'methods') ?? '').split(',').filter(Boolean));
  const entries: unknown[] = [];
  let streamedCount = 0;
  client.on('notification', (method, params, deviceId) => {
    if (methods.size > 0 && !methods.has(method)) return;
    const entry = { timestamp: new Date().toISOString(), deviceId, method, params };
    if (args.options.jsonl) {
      streamedCount++;
      process.stdout.write(`${JSON.stringify(entry)}\n`);
    } else {
      entries.push(entry);
    }
  });
  try {
    await client.connect();
    await new Promise((resolve) => setTimeout(resolve, seconds * 1000));
  } finally {
    await client.close();
  }
  return args.options.jsonl ? { data: { notifications: streamedCount } } : { data: entries };
}
